import { isTodayBetweenDates } from '../utils/dates';

import {
    BannerActionType,
    BannerPositionPlacement,
    BannerPositionScene,
    BannerStatus,
    BannerType,
} from '../types/enums/bannerType.enum';

import { Banner, BannerActionParameters } from '../types/bannerTypes';

import { EmptyBanner } from './emptyBanner';

export function filterBannersBySearchString(
    banners: Banner[],
    searchString: string,
) {
    if (!searchString) {
        return banners;
    }

    const loweredSearchString = searchString.toLowerCase();
    return banners.filter((banner) =>
        banner.name?.toLowerCase()?.includes(loweredSearchString),
    );
}

export function filterBannersByStatus(banners: Banner[], status: string) {
    if (status === 'all') {
        return banners;
    }

    if (status === BannerStatus.DEFAULT) {
        return banners.filter((banner) => banner.isDefault);
    }

    return banners.filter((banner) => {
        const isActive =
            !banner.isDefault &&
            isTodayBetweenDates(banner.activeFromDate, banner.activeToDate);
        return status === BannerStatus.ACTIVE ? isActive : !isActive;
    });
}

export function filterBannersByLocation(
    banners: Banner[],
    filterByLocation: string,
) {
    return banners.filter(
        (banner) => banner.positionScene === filterByLocation,
    );
}

export function moveFilteredListItem<T>(
    items: T[],
    filteredSourceIndex: number,
    filteredDestinationIndex: number,
    filterCondition: (item: T) => boolean,
): number[] {
    let i = 0,
        filteredIndex = 0;
    let source = -1,
        destination = -1;

    while (i < items.length && (source === -1 || destination === -1)) {
        if (filterCondition(items[i])) {
            if (filteredIndex === filteredSourceIndex) source = i;
            if (filteredIndex === filteredDestinationIndex) destination = i;
            filteredIndex++;
        }
        i++;
    }

    if (source === -1 || destination === -1) {
        throw new Error('Source or destination not found');
    }

    return [source, destination];
}

export const getDefaultBannerTypeAndPlacementByScene = (
    scene: Banner['positionScene'],
): {
    type: Banner['type'];
    placement: Banner['positionPlacement'];
} => {
    switch (scene) {
        case BannerPositionScene.START_SCREEN:
            return {
                type: BannerType.MOON,
                placement: BannerPositionPlacement.TOP_LEFT,
            };
        case BannerPositionScene.WORLD_MAP:
            return {
                type: BannerType.LANDSCAPE_SPEECH_BUBBLE,
                placement: BannerPositionPlacement.TOP_LEFT,
            };
        case BannerPositionScene.HD_BUILDING_SELECT:
            return {
                type: BannerType.LANDSCAPE_TOP_PANEL,
                placement: BannerPositionPlacement.FIRST_SELECT_ITEM,
            };
        case BannerPositionScene.HD_CATALOG:
            return {
                type: BannerType.PORTRAIT_LARGE,
                placement: BannerPositionPlacement.SCENE_SPECIFIC,
            };
        case BannerPositionScene.CC_CHARACTER_SELECT:
            return {
                type: BannerType.RECTANGULAR_MEDIUM,
                placement: BannerPositionPlacement.FIRST_SELECT_ITEM,
            };
        case BannerPositionScene.CC_CATALOG:
            return {
                type: BannerType.LANDSCAPE_WIDE,
                placement: BannerPositionPlacement.SCENE_SPECIFIC,
            };
        case BannerPositionScene.OD_CATALOG:
            return {
                type: BannerType.LANDSCAPE_WITH_BUTTON,
                placement: BannerPositionPlacement.BUTTON_RIGHT,
            };
    }

    throw new Error('Unsupported scene');
};

export const getDefaultParametersByActionType = (
    type: Banner['actionType'],
): BannerActionParameters => {
    switch (type) {
        case BannerActionType.OPEN_SCENE:
            return {
                sceneName: '',
                xPosition: 0,
            };
        case BannerActionType.OPEN_TAB:
            return {
                tabStringId: '',
            };
        case BannerActionType.OPEN_DISTRICT:
            return {
                districtId: '',
            };
        case BannerActionType.OPEN_PDP:
            return {
                packId: '',
            };
        case BannerActionType.OPEN_SHOP:
        default:
            return null;
    }
};

export function isPackAction(
    params: BannerActionParameters,
): params is { packId: string } {
    return (
        params !== null &&
        typeof params === 'object' &&
        'packId' in params &&
        typeof (params as { packId?: unknown }).packId === 'string'
    );
}

export function isSceneAction(
    params: BannerActionParameters,
): params is { sceneName: string; xPosition: number } {
    return (
        params !== null &&
        typeof params === 'object' &&
        'sceneName' in params &&
        'xPosition' in params &&
        typeof (params as { sceneName?: unknown }).sceneName === 'string' &&
        typeof (params as { xPosition?: unknown }).xPosition === 'number'
    );
}

export function isTabAction(
    params: BannerActionParameters,
): params is { tabStringId: string } {
    return (
        params !== null &&
        typeof params === 'object' &&
        'tabStringId' in params &&
        typeof (params as { tabStringId?: unknown }).tabStringId === 'string'
    );
}

export function isDistrictAction(
    params: BannerActionParameters,
): params is { districtId: string } {
    return (
        params !== null &&
        typeof params === 'object' &&
        'districtId' in params &&
        typeof (params as { districtId?: unknown }).districtId === 'string'
    );
}

export function hasSubtitle(banner: Banner | EmptyBanner) {
    return banner.type === BannerType.MOON;
}
